/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import debounce from 'lodash/debounce'
import { useQuery } from '@apollo/client'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { DEBOUNCE_DELAY } from 'constants/consts'
import { SEARCH_TEAM_MEMBER } from 'queries/teamMember'
import { CreateCrashCourseInput } from '__generatedTypes__/globalTypes'
import { searchTeamMember, searchTeamMemberVariables } from '__generatedTypes__/searchTeamMember'
import {
  findOneCrashCourse_findOneCrashCourse as CrashCourse,
  findOneCrashCourse_findOneCrashCourse_author as Author,
} from '__generatedTypes__/findOneCrashCourse'
import Input, { InputField } from 'components/Input'
import { DateTimeInputField } from 'components/DateTimeInput'
import { MultiSelectField } from 'components/MultiSelect'
import AdvancedFieldArray from 'components/AdvancedFieldArray'
import { validateRequired, validateUrl } from 'utils/validation'
import { toast } from 'utils/toast'
import RemoveButton from './RemoveButton'
import * as classes from './styles'

export type Props = {
  crashCourseOrSubjectId: CrashCourse | string
  onSubmit: (crashCourse: CreateCrashCourseInput) => Promise<void>
}

const focusOnErrors = createDecorator<CrashCourse, Partial<CrashCourse>>()

export const prepareForSubmit = (values: Partial<CrashCourse>): CreateCrashCourseInput => ({
  additionalInfo: values.additionalInfo,
  author: values.author?.map((author) => ({ id: author.id })),
  courseName: values.courseName ?? '',
  link: values.link ?? '',
  order: values.order,
  price: values.price ?? '',
  startDate: values.startDate?.toISOString() ?? '',
  subject: values.subject ? { id: values.subject.id } : undefined,
  topics: values.topics ?? [],
})

export const getInitialValues = (crashCourseOrSubjectId: CrashCourse | string): Partial<CrashCourse> => {
  if (typeof crashCourseOrSubjectId === 'string') {
    return {
      subject: { id: crashCourseOrSubjectId } as CrashCourse['subject'],
      topics: [''],
      order: 0,
    }
  }

  return {
    ...crashCourseOrSubjectId,
    startDate: new Date(crashCourseOrSubjectId.startDate),
  }
}

export const Form = ({ crashCourseOrSubjectId, onSubmit }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState(getInitialValues(crashCourseOrSubjectId))

  const teamMembersState = useQuery<searchTeamMember, searchTeamMemberVariables>(SEARCH_TEAM_MEMBER, {
    notifyOnNetworkStatusChange: true,
  })

  const teamMembers = teamMembersState.data?.searchTeamMember.items ?? []

  useEffect(() => {
    setInitialValues(getInitialValues(crashCourseOrSubjectId))
  }, [crashCourseOrSubjectId])

  const handleTeamMemberInputChange = useCallback(
    debounce((searchString: string) => {
      teamMembersState.refetch({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const handleSubmit = useCallback(
    async (values: CrashCourse) => {
      try {
        const crashCourseData = prepareForSubmit(values)
        await onSubmit(crashCourseData)
      } catch (e) {
        toast.error('Failed to prepare crash course data for submission')
      }
    },
    [onSubmit]
  )

  return (
    <section>
      <Typography variant="title3" css={classes.title}>
        Crash Courses
      </Typography>
      <div css={classes.wrapper}>
        <h1 css={classes.mainTitle}>{initialValues.id ? 'Edit' : 'Create'} Course</h1>
        <Divider css={classes.divider} />
        <FinalForm<CrashCourse>
          mutators={{ ...arrayMutators }}
          decorators={[focusOnErrors]}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, initialValues, submitting: formSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div css={classes.content}>
                <InputField
                  name="courseName"
                  validate={validateRequired}
                  label="Course Name"
                  placeholder="Enter Course Name"
                  fullWidth
                />
                <DateTimeInputField
                  name="startDate"
                  validate={validateRequired}
                  label="Course Date"
                  placeholder="Enter Course Date"
                  fullWidth
                />
                <InputField
                  name="link"
                  validate={(value) => (!value ? 'Required' : validateUrl(value))}
                  label="Register Link"
                  placeholder="Enter Register Link"
                  fullWidth
                />
                <InputField
                  name="price"
                  validate={validateRequired}
                  label="Price"
                  placeholder="Enter Price"
                  type="number"
                  fullWidth
                />

                <MultiSelectField<Author>
                  name="author"
                  validate={validateRequired}
                  items={teamMembers}
                  onInputChange={handleTeamMemberInputChange}
                  loading={teamMembersState.loading}
                  renderItem={(teamMember) => teamMember.fullName}
                  getItemLabel={(teamMember) => teamMember.fullName}
                  label="Author"
                  placeholder="Choose Author"
                  maxSelectedItemsCount={2}
                  displaySelectedItemsFirst
                  searchable
                  fullWidth
                />

                <AdvancedFieldArray<string>
                  name="topics"
                  validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one topic' : undefined)}
                  minCount={1}
                  itemName="Topic"
                  label="Topics"
                  getEmptyItem={() => ''}
                  noItemsText="No topics added"
                  validateField={validateRequired}
                  renderField={({ name, value, onChange, editable, index, error }) =>
                    editable ? (
                      <Input
                        name={name}
                        value={value ?? ''}
                        onChange={onChange}
                        placeholder={`Enter Topic #${index + 1}`}
                        size="small"
                        color="secondary"
                        error={!!error}
                        fullWidth
                      />
                    ) : (
                      <div css={classes.readOnly}>{value ?? ''}</div>
                    )
                  }
                  initialEditable={!initialValues.id}
                  removeConfirmation
                />

                <InputField
                  name="additionalInfo"
                  label="Additional Info"
                  placeholder="Enter Additional Info"
                  fullWidth
                  multiline
                />

                <div css={classes.buttonsWrapper}>
                  <div>
                    {!!initialValues.id && (
                      <RemoveButton
                        crashCourseId={initialValues.id}
                        subjectId={initialValues?.subject?.id}
                        submitting={formSubmitting || submitting}
                        setSubmitting={setSubmitting}
                        css={classes.removeButton}
                      />
                    )}
                  </div>
                  <div>
                    {/* eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any */}
                    <Link to={-1 as any}>
                      <Button variant="outlined" color="blue" disabled={formSubmitting || submitting} size="large">
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      type="submit"
                      variant="contained"
                      color="blue"
                      disabled={formSubmitting || submitting}
                      size="large"
                    >
                      {initialValues.id ? 'Confirm Edits' : 'Create'}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        />
      </div>
    </section>
  )
}
